import type { FirebaseOptions } from "firebase/app";

/**
 * Resolve public Firebase SDK settings for a promoted dashboard image.
 * Co-pilot must never fall back to a bundled prod/dev project configuration.
 *
 * @param serialized - JSON from the dashboard's runtime configuration
 * @param environment - Runtime application environment
 * @param fallback - Existing Firebase Hosting or local-development settings
 * @returns Validated Firebase web app configuration
 */
export function resolveFirebaseConfig(
  serialized: string | undefined,
  environment: string | undefined,
  fallback: FirebaseOptions,
): FirebaseOptions {
  const isCopilot = environment?.startsWith("co-pilot-") === true;
  if (serialized === undefined) {
    if (isCopilot) {
      throw new Error(
        "Co-pilot requires explicit Firebase runtime configuration",
      );
    }
    return fallback;
  }

  const config: unknown = JSON.parse(serialized);
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    throw new Error("Firebase runtime configuration must be an object");
  }
  const options = config as FirebaseOptions;
  for (const key of [
    "apiKey",
    "appId",
    "projectId",
    "authDomain",
    "storageBucket",
  ] as const) {
    if (typeof options[key] !== "string" || !options[key]?.trim()) {
      throw new Error(`Firebase runtime configuration requires ${key}`);
    }
  }
  if (
    isCopilot &&
    (options.projectId !== "risa-co-pilot" ||
      options.authDomain !== "risa-co-pilot.firebaseapp.com" ||
      options.storageBucket !== "risa-co-pilot.appspot.com")
  ) {
    throw new Error("Co-pilot Firebase settings must belong to risa-co-pilot");
  }
  return options;
}
